import type {
  PayzheBtpBridgeReadiness,
  PayzheBtpLastWriteDiagnostics,
  PayzheBtpStatusPayload,
  PayzheBtpVersionInfo,
  PayzheBtpJobPayload,
  PayzheBtpJobStatus,
} from './types';

type ApiEnvelope<T> = {
  success?: boolean;
  data?: T;
  error?: { code?: string; message?: string } | null;
  payload?: T;
  type?: string;
};

export function unwrapApiPayload<T>(parsed: unknown): T {
  if (typeof parsed !== 'object' || parsed === null) {
    return parsed as T;
  }

  const envelope = parsed as ApiEnvelope<T>;

  if (envelope.success === false) {
    const message =
      envelope.error?.message ??
      (typeof envelope.error === 'string' ? envelope.error : 'payzhe BTP request failed');
    throw new Error(message);
  }

  if (envelope.payload !== undefined) {
    return envelope.payload as T;
  }

  if (envelope.data !== undefined) {
    return envelope.data as T;
  }

  return parsed as T;
}

function normalizeBridgeReadiness(raw: unknown): PayzheBtpBridgeReadiness | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const data = raw as Record<string, unknown>;
  if (typeof data.ready !== 'boolean') return undefined;
  return {
    ready: data.ready,
    httpRunning: typeof data.httpRunning === 'boolean' ? data.httpRunning : undefined,
    webSocketRunning:
      typeof data.webSocketRunning === 'boolean' ? data.webSocketRunning : undefined,
    serviceRunning: typeof data.serviceRunning === 'boolean' ? data.serviceRunning : undefined,
    protocolCompatible:
      typeof data.protocolCompatible === 'boolean' ? data.protocolCompatible : undefined,
    reasons: Array.isArray(data.reasons)
      ? data.reasons.filter((r): r is string => typeof r === 'string')
      : undefined,
  };
}

export function normalizeVersionInfo(raw: unknown): PayzheBtpVersionInfo {
  const data = raw as Record<string, unknown>;
  const compatRaw = data.compatibility as Record<string, unknown> | undefined;
  const compatibility =
    compatRaw && typeof compatRaw.compatible === 'boolean'
      ? {
          webVersion: String(compatRaw.webVersion ?? ''),
          minimumWebVersion: String(compatRaw.minimumWebVersion ?? data.minimumWebVersion ?? '0.0.0'),
          compatible: compatRaw.compatible,
          apiVersion:
            typeof compatRaw.apiVersion === 'string' ? compatRaw.apiVersion : undefined,
          warning: typeof compatRaw.warning === 'string' ? compatRaw.warning : undefined,
        }
      : undefined;

  return {
    name: String(data.name ?? data.application ?? 'payzhe BTP'),
    version: String(data.version ?? '0.0.0'),
    build: Number(data.build ?? 0),
    apiVersion: String(data.apiVersion ?? '1'),
    minimumWebVersion: String(data.minimumWebVersion ?? '0.0.0'),
    compatibility,
    bridgeReady: typeof data.bridgeReady === 'boolean' ? data.bridgeReady : undefined,
    readiness: normalizeBridgeReadiness(data.readiness),
  };
}

export function normalizeJobPayload(raw: unknown): PayzheBtpJobPayload {
  const data = raw as Record<string, unknown>;
  return {
    jobId: String(data.jobId ?? ''),
    requestId: typeof data.requestId === 'string' ? data.requestId : undefined,
    status: String(data.status ?? 'pending') as PayzheBtpJobStatus,
    lastError: typeof data.lastError === 'string' ? data.lastError : undefined,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : undefined,
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : undefined,
    completedAt: typeof data.completedAt === 'string' ? data.completedAt : undefined,
  };
}

function readServiceLifecycle(data: Record<string, unknown>): string | undefined {
  const bg = data.backgroundService as { lifecycle?: string } | undefined;
  if (typeof bg?.lifecycle === 'string') return bg.lifecycle;
  if (typeof data.serviceLifecycle === 'string') return data.serviceLifecycle;
  return undefined;
}

function readServiceRunning(data: Record<string, unknown>): boolean | undefined {
  if (typeof data.serviceRunning === 'boolean') return data.serviceRunning;
  const bg = data.backgroundService as { running?: boolean } | undefined;
  if (typeof bg?.running === 'boolean') return bg.running;
  return undefined;
}

export function normalizeStatusPayload(raw: unknown): PayzheBtpStatusPayload {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error('Invalid status payload');
  }

  const data = raw as Record<string, unknown>;

  if ('app' in data || 'queueSize' in data) {
    const app = (data.app ?? data.printer ?? {}) as Record<string, unknown>;
    return {
      connected: Boolean(app.connected ?? data.connected),
      connectionState: String(app.connectionState ?? data.connectionState ?? 'unknown'),
      modelId: typeof app.modelId === 'string' ? app.modelId : undefined,
      transport: typeof app.transport === 'string' ? app.transport : undefined,
      deviceName: typeof app.deviceName === 'string' ? app.deviceName : undefined,
      queueLength: Number(data.queueSize ?? app.queueLength ?? 0),
      serverState: String(data.serverState ?? 'unknown'),
      serviceRunning: readServiceRunning(data),
      serviceLifecycle: readServiceLifecycle(data),
      heartbeatAt: typeof app.heartbeatAt === 'string' ? app.heartbeatAt : undefined,
      bluetoothAdapterState: String(app.bluetoothAdapterState ?? 'unknown'),
      appVersion: String(app.appVersion ?? data.appVersion ?? ''),
      lastWrite: normalizeLastWriteDiagnostics(data.lastWrite),
    };
  }

  return {
    connected: Boolean(data.connected),
    connectionState: String(data.connectionState ?? 'unknown'),
    modelId: typeof data.modelId === 'string' ? data.modelId : undefined,
    transport: typeof data.transport === 'string' ? data.transport : undefined,
    deviceName: typeof data.deviceName === 'string' ? data.deviceName : undefined,
    batteryPercent: typeof data.batteryPercent === 'number' ? data.batteryPercent : undefined,
    signalStrength: typeof data.signalStrength === 'number' ? data.signalStrength : undefined,
    firmware: typeof data.firmware === 'string' ? data.firmware : undefined,
    connectionDurationMs:
      typeof data.connectionDurationMs === 'number' ? data.connectionDurationMs : undefined,
    queueLength: Number(data.queueLength ?? 0),
    currentJobId: typeof data.currentJobId === 'string' ? data.currentJobId : undefined,
    serverState: String(data.serverState ?? 'unknown'),
    serviceRunning: readServiceRunning(data),
    serviceLifecycle: readServiceLifecycle(data),
    heartbeatAt: typeof data.heartbeatAt === 'string' ? data.heartbeatAt : undefined,
    bluetoothAdapterState: String(data.bluetoothAdapterState ?? 'unknown'),
    appVersion: String(data.appVersion ?? ''),
    lastWrite: normalizeLastWriteDiagnostics(data.lastWrite),
    bridgeReady: typeof data.bridgeReady === 'boolean' ? data.bridgeReady : undefined,
    readiness: normalizeBridgeReadiness(data.readiness),
  };
}

export function normalizeLastWriteDiagnostics(raw: unknown): PayzheBtpLastWriteDiagnostics | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const data = raw as Record<string, unknown>;
  return {
    jobId: typeof data.jobId === 'string' ? data.jobId : null,
    jobBytes: typeof data.jobBytes === 'number' ? data.jobBytes : null,
    writeDurationMs: typeof data.writeDurationMs === 'number' ? data.writeDurationMs : null,
    writeResult:
      data.writeResult === 'success' || data.writeResult === 'failed' ? data.writeResult : null,
    lastError: typeof data.lastError === 'string' ? data.lastError : null,
    retryCount: typeof data.retryCount === 'number' ? data.retryCount : null,
    transport: typeof data.transport === 'string' ? data.transport : null,
    bridgeProfile: typeof data.bridgeProfile === 'string' ? data.bridgeProfile : null,
    gattServiceUuid: typeof data.gattServiceUuid === 'string' ? data.gattServiceUuid : null,
    mtu: typeof data.mtu === 'number' ? data.mtu : null,
    effectiveChunkSize: typeof data.effectiveChunkSize === 'number' ? data.effectiveChunkSize : null,
    effectiveWriteDelayMs:
      typeof data.effectiveWriteDelayMs === 'number' ? data.effectiveWriteDelayMs : null,
    postWriteSettleMsConfigured:
      typeof data.postWriteSettleMsConfigured === 'number' ? data.postWriteSettleMsConfigured : null,
    postWriteSettleMsApplied:
      typeof data.postWriteSettleMsApplied === 'number' ? data.postWriteSettleMsApplied : null,
    androidApiLevel: typeof data.androidApiLevel === 'number' ? data.androidApiLevel : null,
    vendorProfile: typeof data.vendorProfile === 'string' ? data.vendorProfile : null,
    compatibilityTier: typeof data.compatibilityTier === 'string' ? data.compatibilityTier : null,
  };
}
